import { useState, useCallback } from 'react';
import { collectDiagnosticData } from '@/utils/diagnostics';
import { submitFeedback } from '@/api/feedback';
import { storage } from '@/game-engine/storage';
import { useUser } from '@/contexts/UserContext';
import type { FeedbackRequest } from '@/types/api';

export interface UseDiagnosticsBugReportOptions {
  /** Feature/component identifier (e.g., 'HighScoresPanel', 'VoiceInput') */
  feature: string;
  /** Error object to include in report */
  error?: Error | null;
  /** Current game state snapshot */
  gameState?: any;
  /** User preferences */
  preferences?: any;
  /** Any additional context to include */
  additionalContext?: any;
}

export interface UseDiagnosticsBugReportResult {
  submitReport: (userDescription?: string) => Promise<void>;
  isSubmitting: boolean;
  isSubmitted: boolean;
  submitError: string | null;
  deliveryMethod: 'api' | 'slack' | 'database' | 'localStorage' | null;
  reset: () => void;
}

/**
 * Hook for submitting diagnostic bug reports from error states.
 * Wraps the existing diagnostics system with error-specific context.
 */
export function useDiagnosticsBugReport({
  feature,
  error,
  gameState = {},
  preferences = {},
  additionalContext = {},
}: UseDiagnosticsBugReportOptions): UseDiagnosticsBugReportResult {
  const { username } = useUser();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [deliveryMethod, setDeliveryMethod] = useState<'api' | 'slack' | 'database' | 'localStorage' | null>(null);

  const reset = useCallback(() => {
    setIsSubmitted(false);
    setSubmitError(null);
    setDeliveryMethod(null);
  }, []);

  const submitReport = useCallback(
    async (userDescription = '') => {
      setIsSubmitting(true);
      setSubmitError(null);

      try {
        // Collect full diagnostics
        const diagnostics = await collectDiagnosticData(gameState, preferences);

        // Build error context
        const errorContext = {
          feature,
          route: window.location.pathname,
          timestamp: new Date().toISOString(),
          ...(error && {
            errorMessage: error.message,
            errorStack: error.stack,
            errorName: error.name,
          }),
          ...additionalContext,
        };

        // Construct bug report payload
        const bugReport: FeedbackRequest = {
          username: username || 'anonymous',
          type: 'bug',
          message: userDescription || `Error in ${feature}${error ? `: ${error.message}` : ''}`,
          url: window.location.href,
          userAgent: navigator.userAgent,
          diagnostics: {
            ...diagnostics,
            errorContext,
          } as any,
        };

        // Try API submission first
        try {
          const result = await submitFeedback(bugReport);

          if (result.deliveredVia?.api) {
            setDeliveryMethod('api');
          } else if (result.deliveredVia?.slack) {
            setDeliveryMethod('slack');
          } else {
            setDeliveryMethod('api');
          }

          setIsSubmitted(true);
        } catch (apiError) {
          console.warn('API submission failed, falling back to localStorage:', apiError);

          // Fallback to localStorage
          const savedLocally = await storage.saveFeedback(bugReport);

          if (savedLocally) {
            setDeliveryMethod('localStorage');
            setIsSubmitted(true);
          } else {
            throw new Error('Failed to save report locally');
          }
        }
      } catch (err: any) {
        console.error('Failed to submit diagnostic bug report:', err);
        setSubmitError(err.message || 'Failed to submit bug report');
      } finally {
        setIsSubmitting(false);
      }
    },
    [feature, error, gameState, preferences, additionalContext, username]
  );

  return {
    submitReport,
    isSubmitting,
    isSubmitted,
    submitError,
    deliveryMethod,
    reset,
  };
}
